/**
 * Shared building blocks for AdminUserDetailModal and AdminAuditTab.
 */

import React, { useCallback, useState } from 'react';
import { CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AdminAuditLog } from '@/services/adminService';

export function formatDate(dateStr?: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Badge({ label, className }: { label: string; className?: string }) {
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', className || 'bg-muted text-muted-foreground')}>
      {label}
    </span>
  );
}

export function Section({ title, icon, actions, children }: {
  title: string;
  icon: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 text-sm py-1">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-foreground font-medium text-right break-all">{value}</span>
    </div>
  );
}

export const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  TRIALING: 'bg-yellow-100 text-yellow-800',
  CANCELED: 'bg-red-100 text-red-800',
  PAST_DUE: 'bg-orange-100 text-orange-800',
  PENDING: 'bg-gray-100 text-gray-800',
  PAID: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
};

// ============================================================================
// Inline success/error feedback (the admin panel has no toast library)
// ============================================================================

export type Feedback = { kind: 'success' | 'error'; message: string } | null;

export function useFeedback() {
  const [feedback, setFeedback] = useState<Feedback>(null);
  const success = useCallback((message: string) => setFeedback({ kind: 'success', message }), []);
  const error = useCallback((err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    setFeedback({ kind: 'error', message });
  }, []);
  const clear = useCallback(() => setFeedback(null), []);
  return { feedback, success, error, clear };
}

export function FeedbackBanner({ feedback, onClose }: { feedback: Feedback; onClose: () => void }) {
  if (!feedback) return null;
  const isError = feedback.kind === 'error';
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-2 text-sm p-3 rounded-lg',
        isError ? 'bg-destructive/10 text-destructive' : 'bg-green-500/10 text-green-700 dark:text-green-400',
      )}
    >
      {isError ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> : <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />}
      <span className="flex-1">{feedback.message}</span>
      <button onClick={onClose} className="opacity-70 hover:opacity-100" aria-label="Fechar">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ============================================================================
// Audit log list
// ============================================================================

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  instagram_refresh: 'Atualizar do Instagram',
  profile_edit: 'Editar perfil',
  instagram_relink: 'Trocar Instagram',
  instagram_unlink: 'Desvincular Instagram',
  email_change: 'Trocar e-mail',
  email_alias_remove: 'Remover e-mail anterior',
  password_reset_link: 'Link de redefinição',
  password_reset_temporary: 'Senha temporária',
  help_reply: 'Resposta no chat de dúvidas',
  impersonation_start: 'Entrou como usuário',
  impersonation_end: 'Saiu do modo suporte',
  impersonation_blocked: 'Ação bloqueada (modo suporte)',
};

const AUDIT_STATUS_COLORS: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  noop: 'bg-gray-100 text-gray-800',
  dry_run: 'bg-blue-100 text-blue-800',
};

export function AuditLogList({ logs, showTarget = false }: { logs: AdminAuditLog[]; showTarget?: boolean }) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (logs.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhuma ação registrada.</p>;
  }

  return (
    <div className="divide-y divide-border">
      {logs.map((log) => {
        const expanded = expandedId === log.id;
        const actor = log.actor_type === 'admin' ? `admin ${log.actor_id?.slice(0, 8)}` : `bot ${log.actor_key_id?.slice(0, 8)}`;
        return (
          <div key={log.id} className="py-2 text-sm">
            <button
              className="w-full flex flex-wrap items-center gap-2 text-left"
              onClick={() => setExpandedId(expanded ? null : log.id)}
            >
              <span className="text-xs text-muted-foreground w-28 shrink-0">{formatDate(log.created_at)}</span>
              <Badge label={AUDIT_ACTION_LABELS[log.action] || log.action} className="bg-primary/10 text-primary" />
              <Badge label={log.status} className={AUDIT_STATUS_COLORS[log.status]} />
              <span className="text-xs text-muted-foreground">{actor}</span>
              {showTarget && (
                <span className="text-xs text-muted-foreground font-mono">alvo {log.target_user_id.slice(0, 8)}</span>
              )}
            </button>
            {log.error && <p className="text-xs text-destructive mt-1 ml-[7.5rem]">{log.error}</p>}
            {expanded && (
              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-h-60">
                {JSON.stringify({ before: log.before, after: log.after, metadata: log.metadata }, null, 2)}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}
